import { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';

const VENDOR_LOGOS = [
  { name: 'Epic', src: '/screenshots/epic-logo.svg', shape: 'square' },
  { name: 'Cerner', src: '/screenshots/2011.cerner.logo.png', shape: 'rect' },
  { name: 'athenahealth', src: '/screenshots/665115ea8a2c94a8a86b4399_hiresathena.png', shape: 'rect' },
  { name: 'Allscripts', src: '/screenshots/allscripts-logo.png', shape: 'rect' },
  { name: 'eClinicalWorks', src: '/screenshots/eclinicalworks-1.svg', shape: 'rect' },
  { name: 'NextGen', src: '/screenshots/nxgn-ba9bef15.png', shape: 'square' },
  { name: 'DrChrono', src: '/screenshots/drchrono_logo_black_1400x399.4cd8bfb2149e.png', shape: 'rect' },
];

const CENTRAL_LOGO = '/screenshots/ariaone-emblem_white.png';

const IntegrationAnimation = ({ className = '' }) => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-50px" });
  const [containerWidth, setContainerWidth] = useState(800);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.getBoundingClientRect().width);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const W = containerWidth;
  const H = Math.min(W * 0.7, 480);
  const cx = W / 2;
  const cy = H / 2;
  const radius = Math.min(W, H) * 0.36;

  const vendorPositions = VENDOR_LOGOS.map((_, i) => {
    const angle = (i / VENDOR_LOGOS.length) * Math.PI * 2 - Math.PI / 2;
    return { x: cx + Math.cos(angle) * radius, y: cy + Math.sin(angle) * radius };
  });

  return (
    <div ref={containerRef} className={`relative ${className}`} style={{ height: H }} data-testid="integration-animation">
      {/* SVG lines and particles */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="igLineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(107,92,231,0.25)" />
            <stop offset="100%" stopColor="rgba(107,92,231,0.5)" />
          </linearGradient>
          <radialGradient id="igCenterGlow">
            <stop offset="0%" stopColor="rgba(107,92,231,0.15)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r="70" fill="url(#igCenterGlow)" />
        {vendorPositions.map((pos, i) => (
          <motion.line key={`l-${i}`} x1={pos.x} y1={pos.y} x2={cx} y2={cy}
            stroke="url(#igLineGrad)" strokeWidth="1.5" strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={isInView ? { pathLength: 1, opacity: 0.5 } : {}}
            transition={{ delay: 0.3 + i * 0.1, duration: 0.8, ease: "easeOut" }}
          />
        ))}
        {vendorPositions.map((pos, i) => (
          <motion.circle key={`p-${i}`} r="3" fill="#6b5ce7"
            initial={{ opacity: 0 }}
            animate={isInView ? { cx: [pos.x, cx], cy: [pos.y, cy], opacity: [0, 1, 0] } : {}}
            transition={{ delay: 1.2 + i * 0.2, duration: 1.5, repeat: Infinity, repeatDelay: 2 + i * 0.3, ease: "easeInOut" }}
          />
        ))}
      </svg>

      {/* Vendor logo cards */}
      {VENDOR_LOGOS.map((vendor, i) => {
        const pos = vendorPositions[i];
        const isRect = vendor.shape === 'rect';
        return (
          <motion.div key={vendor.name}
            className="absolute flex items-center justify-center"
            style={{
              left: pos.x, top: pos.y, transform: 'translate(-50%, -50%)',
              width: isRect ? 72 : 56, height: isRect ? 44 : 56,
              borderRadius: isRect ? 10 : 14,
              background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1 + i * 0.1, type: "spring", stiffness: 200 }}
            whileHover={{ scale: 1.15, borderColor: 'rgba(107,92,231,0.5)' }}
            data-testid={`vendor-logo-${vendor.name.toLowerCase()}`}
          >
            <img src={vendor.src} alt={vendor.name}
              className="max-w-[70%] max-h-[70%] object-contain"
              style={{ filter: 'brightness(0) invert(1)', opacity: 0.7 }}
            />
          </motion.div>
        );
      })}

      {/* Central AriaOne emblem — white bg, black logo, properly centered */}
      <motion.div
        className="absolute flex items-center justify-center"
        style={{
          left: '50%', top: '50%',
          marginLeft: -44, marginTop: -44,
          width: 88, height: 88, borderRadius: 22,
          background: '#ffffff',
          boxShadow: '0 0 50px rgba(107,92,231,0.25), 0 8px 30px rgba(0,0,0,0.3)',
        }}
        initial={{ opacity: 0, scale: 0 }}
        animate={isInView ? { opacity: 1, scale: 1 } : {}}
        transition={{ delay: 0.5, type: "spring", stiffness: 150 }}
        data-testid="central-ariaone-emblem"
      >
        <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 3, repeat: Infinity }}>
          <img src="/screenshots/ariaone-emblem_white.png" alt="AriaOne"
            className="w-14 h-14 object-contain"
            style={{ filter: 'brightness(0)' }}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default IntegrationAnimation;
